import { Card, Stack, Typography } from '@mui/material';
import { PerformerRequest } from '@silbo/core';
import { ArrowRightIcon, ChevronSmallRightIcon, Emoji, palette } from '@silbo/ui';
import { Link } from '@tanstack/react-router';
import { format, isSameDay } from 'date-fns';
import { useQueryQueueContext } from '../../../contexts/QueryQueueContext';
import { getPatientEmojiIcon } from '../../../utils/get-patient-emoji-icon';
import { getRequestReason } from '../utils/get-request-reason';
import { getRequestType } from '../utils/get-request-type';
import RequestTypeIcon from './RequestTypeIcon';

type RequestCardProps = {
  request: PerformerRequest;
};

const RequestCard = ({ request }: RequestCardProps) => {
  const reason = getRequestReason(request);
  const type = getRequestType(request);
  const { getPendingRequestAction } = useQueryQueueContext();

  const pendingAction = getPendingRequestAction(request.id);
  const hasPendingAction = pendingAction !== null;

  if (!request.request.visit || !type) {
    return null;
  }

  return (
    <Link
      to="/bed-management/requests/$id"
      params={{ id: request.id }}
      style={{ textDecoration: 'none', color: 'inherit' }}
      disabled={hasPendingAction}
    >
      <Card
        elevation={1}
        sx={{
          paddingX: 3,
          paddingY: 4,
          cursor: 'pointer',
          '&:hover': { elevation: 3 },
          borderWidth: hasPendingAction ? '2px' : undefined,
          borderColor: hasPendingAction
            ? pendingAction === 'accept'
              ? palette.success.main
              : palette.error.main
            : undefined,
          opacity: hasPendingAction ? 0.5 : 1,
        }}
      >
        <Stack direction="row" alignItems="center" gap={4}>
          <Stack spacing={2} alignItems="center">
            <RequestTypeIcon type={type} />
            <Stack alignItems="center">
              {request.request.createdAt && !isSameDay(request.request.createdAt, new Date()) && (
                <Typography variant="body2" color="secondary">
                  {format(request.request.createdAt, 'dd/MM')}
                </Typography>
              )}
              {request.request.createdAt && (
                <Typography variant="body2" color="secondary">
                  {format(request.request.createdAt, 'HH:mm')}
                </Typography>
              )}
            </Stack>
          </Stack>
          <Stack spacing={2} justifyContent="space-between" flex={1}>
            <Stack direction="row" alignItems="center" gap={2}>
              <Emoji
                name={getPatientEmojiIcon(request.request.visit.gender, request.request.visit.age)}
                bgColor={palette.background.default}
              />
              <Typography fontWeight={600}>{request.request.visit.fullName}</Typography>
            </Stack>
            {reason && <Typography>{reason}</Typography>}
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography color="secondary">
                {request.request.originAccommodation?.unit?.name || request.request.directAdmissionOrigin || '-'}
              </Typography>
              <ArrowRightIcon sx={{ color: palette.text.secondary }} />
              <Typography color="secondary">{request.performer.name}</Typography>
            </Stack>
          </Stack>
          <ChevronSmallRightIcon color="secondary" />
        </Stack>
      </Card>
    </Link>
  );
};

export default RequestCard;
